import Modal from "../../components/Misc/Modal";
import { Icon } from "@iconify/react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AccountSearchModal from "./ModalContents/SearchModal";
import { useDispatch, useSelector } from "react-redux";
import { getSites, searchAccountsData, bulkSweepAccounts } from "../../service";
import { setLoading, updateAccount, updateSites } from "../../redux/actions";
import { useCustomSnackbar } from "../../components/Misc/useSnackbar";
import { saveAs } from "file-saver";
import { unparse } from "papaparse";
import * as XLSX from 'xlsx';

const AccountHeader = ({ paginationModel }) => {
  const [show, setShow] = useState({
    search: false,
    add: false,
  });
  const { page, pageSize } = paginationModel;
  const [formData, setData] = useState({
    limit: pageSize,
    page: page + 1,
    tags: [],
  });
  const { user } = useSelector((state) => state.user);
  const showModal = (key) => {
    setShow({ ...show, [key]: true });
  };

  const hideModal = (key) => {
    setShow({ ...show, [key]: false });
  };

  const dispatch = useDispatch();

  const onClick = () => {
    setData({ limit: pageSize, page: page + 1, tags: [] });
    searchAccounts();
  };
  const isInitialRender = useRef(true);
  const fileInputRef = useRef(null);
  useEffect(() => {
    if (!isInitialRender.current) {
      searchAccounts();
    } else {
      isInitialRender.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);
  const accountsData = useSelector((state) => state.account);
  const onChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };
  const toast = useCustomSnackbar();
  const { sites } = useSelector((state) => state.account);
  useEffect(() => {
    if (sites.length === 0) {
      getSiteData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getSiteData = async () => {
    dispatch(setLoading({ stat: true, text: "Getting Site Data" }));
    const { status, data: sites } = await getSites();
    dispatch(setLoading(false));
    if (status) {
      const uniqueOptionsMap = new Map();
      for (const option of sites) {
        const { description } = option;
        if (!uniqueOptionsMap.has(description)) {
          uniqueOptionsMap.set(description, option);
        }
      }
      const uniqueOptions = Array.from(uniqueOptionsMap.values());
      dispatch(updateSites(uniqueOptions));
    } else {
      dispatch(updateSites([]));
      toast({
        type: "warning",
        message: "Can not get the sites data.",
      });
    }
  };
  const searchAccounts = async () => {
    hideModal("search");
    dispatch(setLoading({ stat: true, text: "Searching Accounts" }));
    let userData = localStorage.getItem("userData");
    userData = JSON.parse(userData);
    const tags = userData?.tags || [];
    const accountData = await searchAccountsData({
      formData: {
        ...formData,
        limit: pageSize,
        page: page + 1,
        tags: user.role === "admin" ? formData.tags.join() : "",
      },
      tags: user.role === "admin" ? null : user.tags || tags,
    });
    dispatch(setLoading(false));
    dispatch(updateAccount(accountData.accounts || {}));
  };

  const handleExport = () => {
    const { accounts } = accountsData.accounts;
    if (!accounts.length) {
      return toast({
        message: "There are no accounts to export",
        type: "error",
      });
    }
    dispatch(setLoading({ stat: true, text: "Preparing to export." }));
    let userData = localStorage.getItem("userData");
    userData = JSON.parse(userData);
    const tags = userData?.tags || [];
    searchAccountsData({
      formData: {
        ...formData,
        limit: "exporting",
        tags: user.role === "admin" ? formData.tags.join() : "",
      },
      tags: user.role === "admin" ? null : user.tags || tags,
    }).then((res) => {
      const { accounts } = res;
      dispatch(setLoading(false));
      if (accounts.accounts.length) {
        const exportData = accounts.accounts.map((item) => ({
          username: item.username || "N/A",
          password: item.password || "N/A",
          site: item.site?.description || "N/A",
        }));
        const csv = unparse(exportData);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, "data.csv");
      } else {
        toast({
          message: "There are no account to export.",
          type: "error",
        });
      }
    });
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return toast({
        message: "Please select a file to import",
        type: "error",
      });
    }
    const reader = new FileReader();

    reader.onload = (e) => {
      const binaryStr = e.target.result;
      const workbook = XLSX.read(binaryStr, { type: 'binary' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      sweepAccounts(jsonData.map(x => x[0]).filter(x => x && x.includes('@')))
    };
    reader.readAsBinaryString(file);
    event.target.value = '';
  };

  const sweepAccounts = (data) => {
    dispatch(setLoading({ stat: true, text: "Wait a moment." }));
    bulkSweepAccounts(data).then((result) => {
      const { data: accounts } = result;
      exportXlsxData(accounts || [])
    }).catch((err) => {
      toast({
        message: "Something went wrong. Try again later.",
        type: "error",
      });
    });
  }

  const exportXlsxData = (jsonData) => {
    dispatch(setLoading(false));
    if (jsonData.length === 0) return toast({ message: 'There is no line to export' })
    const worksheetToExport = XLSX.utils.aoa_to_sheet(jsonData);
    const workbookToExport = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbookToExport, worksheetToExport, 'Sheet1');
    XLSX.writeFile(workbookToExport, 'exported_data.xlsx');
  }

  const importFile = () => {
    fileInputRef.current.click();
  }
  return (
    <div className="flex items-center">
      <p className="text-lg font-500 font-semibold mr-auto">Account List</p>
      {user.role === "admin" && (
        <Link
          to="#"
          onClick={onClick}
          className="inline-flex items-center justify-center gap-2.5 rounded-full bg-black py-2 px-5 text-center font-medium text-white hover:bg-opacity-90 ml-auto"
        >
          <Icon icon="wpf:search" /> Get All
        </Link>
      )}
      <Link
        to="#"
        onClick={() => showModal("search")}
        className="inline-flex items-center justify-center gap-2.5 rounded-full bg-black py-2 px-5 text-center font-medium text-white hover:bg-opacity-90 ml-4"
      >
        <Icon icon="ri:user-search-fill" /> Search
      </Link>
      <Link
        to="/account/add"
        // onClick={() => toggleModal('add')}
        className="inline-flex items-center justify-center gap-2.5 rounded-full bg-black py-2 px-5 text-center font-medium text-white hover:bg-opacity-90 ml-4"
      >
        <Icon icon="material-symbols:group-add-outline" /> Add
      </Link>
      {/* <CsvDownloader
        columns={[
          {
            id: "username",
            displayName: "First column",
          },
          {
            id: "password",
            displayName: "Second column",
          },
        ]}
        datas={handleExport}
        filename="myfile"
        extension=".csv" 
      >*/}
      <button
        to="#"
        onClick={handleExport}
        className="inline-flex items-center justify-center gap-2.5 rounded-full bg-black py-2 px-5 text-center font-medium text-white hover:bg-opacity-90 ml-4"
      >
        <Icon icon="icomoon-free:download" /> Export
      </button>
      <button
        to="#"
        onClick={importFile}
        className="inline-flex items-center justify-center gap-2.5 rounded-full bg-black py-2 px-5 text-center font-medium text-white hover:bg-opacity-90 ml-4"
      >
        <Icon icon="icomoon-free:upload" /> Bulk Sweep
      </button>
      <input
        type="file"
        accept=".xlsx, .xls"
        onChange={handleFileUpload}
        // onInput={handleFileUpload}
        ref={fileInputRef}
        style={{ visibility: 'hidden', width: '0px' }}  
      />
      {/* </CsvDownloader> */}
      <Modal show={show.search}>
        <AccountSearchModal
          onChange={onChange}
          formData={formData}
          onSubmit={searchAccounts}
          onCancel={() => hideModal("search")}
        />
      </Modal>
    </div>
  );
};

export default AccountHeader;
